import { z } from 'zod'

import { missionRouteGuideSchema } from './routePaths.js'

// Missions terrain V1 (migrations 20261022010000_missions_field_v1 et 20261031010000_missions_detection_v1) : contrats
// des fonctions serveur appelées par la
// tablette (reca-operator-v3) et par le bureau (RECA App V3), plus les règles pures partagées (compteur d'heures,
// durées, segmentation de la trace GPS). Aucune dépendance UI, Supabase ou environnementale.

/** Maintien continu exigé pour confirmer le travail d'une résidence. */
export const FIELD_MISSION_HOLD_MS = 3000 as const
/** Maintien continu exigé pour démarrer une mission (ADR-019 : 2 s, alternative accessible par confirmation). */
export const FIELD_MISSION_START_HOLD_MS = 2000 as const
/** Cadence visée d'un relevé de position, application au premier plan. */
export const FIELD_GPS_SAMPLE_INTERVAL_MS = 30_000 as const
/** Au-delà, une position n'est plus une mesure « actuelle » : elle n'est jamais enregistrée comme nouvelle. */
export const FIELD_GPS_MAX_FIX_AGE_MS = 30_000 as const
/** Taille maximale d'un lot de positions accepté par le serveur. */
export const FIELD_POSITIONS_BATCH_MAX = 200 as const

// Postgres sérialise `timestamptz` en ISO 8601 avec décalage et jusqu'aux microsecondes.
const timestampSchema = z.string().refine((value) => !Number.isNaN(Date.parse(value)), 'Horodatage invalide')
const nullableTimestampSchema = timestampSchema.nullable()
const uuidSchema = z.uuid()

export const fieldMissionStatusSchema = z.enum(['IN_PROGRESS', 'PAUSED', 'COMPLETED', 'CANCELLED'])
export type FieldMissionStatus = z.infer<typeof fieldMissionStatusSchema>

export const fieldItemStatusSchema = z.enum(['WAITING', 'COMPLETED', 'PROBLEM'])
export type FieldItemStatus = z.infer<typeof fieldItemStatusSchema>

export const fieldProblemReasonSchema = z.enum(['VEHICLE_IN_DRIVEWAY', 'ACCESS_BLOCKED', 'OBSTACLE', 'OTHER'])
export type FieldProblemReason = z.infer<typeof fieldProblemReasonSchema>

export const fieldProblemReasonLabels: Record<FieldProblemReason, string> = {
  VEHICLE_IN_DRIVEWAY: 'Véhicule dans l’entrée',
  ACCESS_BLOCKED: 'Accès bloqué',
  OBSTACLE: 'Obstacle',
  OTHER: 'Autre',
}

export const fieldItemEventKindSchema = z.enum([
  'COMPLETED',
  'PROBLEM_REPORTED',
  'PROBLEM_RESOLVED',
  'CLEARED',
  'REOPENED',
])

/** Pourquoi le démarrage est impossible; chaque code a une consigne de correction côté interface. */
export const fieldStartBlockerSchema = z.enum([
  'ROUTE_MISSING',
  'ROUTE_UNAVAILABLE',
  'ROUTE_EMPTY',
  'ROUTE_IN_USE',
  'EQUIPMENT_MISSING',
  'EQUIPMENT_UNAVAILABLE',
  'EQUIPMENT_IN_USE',
  'OPERATOR_IN_OTHER_MISSION',
])
export type FieldStartBlocker = z.infer<typeof fieldStartBlockerSchema>

export const fieldProgressSchema = z.object({
  total: z.number().int().nonnegative(),
  completed: z.number().int().nonnegative(),
  problem: z.number().int().nonnegative(),
  waiting: z.number().int().nonnegative(),
  /** Résidences prises en charge par une autre mission (renfort, réattribution) : hors des restantes. */
  transferred: z.number().int().nonnegative().optional(),
})
export type FieldProgress = z.infer<typeof fieldProgressSchema>

export const hourReadingSchema = z.object({
  value: z.number().nonnegative(),
  kind: z.enum(['START', 'END', 'CORRECTION', 'REPLACEMENT']),
  recordedAt: timestampSchema,
  missionId: uuidSchema.nullable(),
})
export type HourReading = z.infer<typeof hourReadingSchema>

// ------------------------------------------------------------------ Détection (ADR-018, migration 20261031010000)

export const detectionModeSchema = z.enum(['OFF', 'OBSERVATION', 'AUTO'])
export type DetectionMode = z.infer<typeof detectionModeSchema>

/**
 * Paramètres du moteur de détection « drapeau + zone de travail ». Le serveur vérifie leur présence, leurs bornes et
 * l'ordre capture < travail < sortie; leurs valeurs ne sont pas homologuées (calibration terrain).
 */
export const detectionParametersSchema = z
  .object({
    captureRadiusM: z.number().positive(),
    workRadiusM: z.number().positive(),
    exitDistanceM: z.number().positive(),
    minInterventionS: z.number().nonnegative(),
    minMovingShare: z.number().min(0).max(1),
    movementWindowS: z.number().positive(),
    stationaryJitterM: z.number().nonnegative(),
    captureConfirmCount: z.number().int().min(1),
    captureConfirmWindowS: z.number().nonnegative(),
    exitConfirmCount: z.number().int().min(1),
    exitConfirmWindowS: z.number().nonnegative(),
    maxAccuracyM: z.number().positive(),
    maxFixAgeS: z.number().positive(),
    maxGapS: z.number().positive(),
    maxSpeedMps: z.number().positive(),
  })
  .refine((value) => value.captureRadiusM < value.workRadiusM && value.workRadiusM < value.exitDistanceM, {
    message: 'Rayons incohérents : capture < travail < sortie',
  })
export type DetectionParameters = z.infer<typeof detectionParametersSchema>

/** Profil figé dans la mission au démarrage (`null` : aucun profil, détection OFF). */
export const missionDetectionProfileSnapshotSchema = z.object({
  id: uuidSchema,
  version: z.number().int().positive(),
  label: z.string(),
  mode: detectionModeSchema,
  /** Validé par RECA sur le terrain : condition du mode AUTO. */
  certified: z.boolean(),
  parameters: detectionParametersSchema,
})
export type MissionDetectionProfileSnapshot = z.infer<typeof missionDetectionProfileSnapshotSchema>

export const detectionProfileSchema = missionDetectionProfileSnapshotSchema.extend({
  reason: z.string().nullable(),
  createdAt: timestampSchema,
  createdByName: z.string().nullable(),
})
export type DetectionProfile = z.infer<typeof detectionProfileSchema>

/** `mission_detection_profiles_v1` : profil actif (dernière version) et historique, plus récent d'abord. */
export const detectionProfilesResultSchema = z.object({
  active: detectionProfileSchema.nullable(),
  history: z.array(detectionProfileSchema),
})
export type DetectionProfilesResult = z.infer<typeof detectionProfilesResultSchema>

/** Commande de `set_mission_detection_profile_v1` : `expectedVersion` = version active lue (null si aucune). */
export interface SetDetectionProfileCommand {
  expectedVersion: number | null
  label: string
  mode: DetectionMode
  certified: boolean
  parameters: DetectionParameters
  reason?: string
}

/** Drapeau en vigueur au démarrage, copié dans l'instantané de la résidence (`null` : aucun drapeau). */
export const fieldItemFlagSchema = z.object({
  latitude: z.number(),
  longitude: z.number(),
  source: z.string().nullable(),
  version: z.number().int().nonnegative(),
})
export type FieldItemFlag = z.infer<typeof fieldItemFlagSchema>

/** Origine d'une terminaison : maintien de 3 s, moteur (mode AUTO seulement), correction motivée. */
export const completionSourceSchema = z.enum(['MANUAL', 'AUTO', 'CORRECTION'])
export type CompletionSource = z.infer<typeof completionSourceSchema>

/** Taille maximale d'un lot d'événements de détection accepté par le serveur. */
export const DETECTION_EVENTS_BATCH_MAX = 200 as const

export const detectionEventKindSchema = z.enum([
  'CANDIDATE',
  'CANDIDATE_LOST',
  'CAPTURED',
  'AMBIGUITY',
  'AMBIGUITY_CLEARED',
  'COMPLETED',
  'VISIT_NOT_ADMISSIBLE',
  'PROBLEM_REPORTED',
  'REOPENED',
  'DETECTION_SUSPENDED',
  'DETECTION_RESUMED',
  'OBSERVATION_IGNORED',
  'PAUSED',
  'RESUMED',
  'ACTION_REFUSED',
])
export type DetectionEventKind = z.infer<typeof detectionEventKindSchema>

/** Événement du moteur envoyé par `record_mission_detection_events_v1` (identifiant produit par la tablette). */
export const detectionEventInputSchema = z.object({
  id: uuidSchema,
  itemId: uuidSchema.nullable().optional(),
  kind: detectionEventKindSchema,
  occurredAt: timestampSchema,
  engineVersion: z.string().min(1).max(40),
  payload: z.record(z.string(), z.unknown()).optional(),
})
export type DetectionEventInput = z.infer<typeof detectionEventInputSchema>

export const detectionEventRejectionReasonSchema = z.enum([
  'INVALID',
  'ID_CONFLICT',
  'ITEM_NOT_IN_MISSION',
  'FUTURE',
  'BEFORE_START',
  'AFTER_END',
  'DETECTION_OFF',
])

export const detectionEventsAckSchema = z.object({
  acknowledged: z.array(uuidSchema),
  rejected: z.array(z.object({ id: uuidSchema, reason: detectionEventRejectionReasonSchema })),
  receivedAt: timestampSchema,
})
export type DetectionEventsAck = z.infer<typeof detectionEventsAckSchema>

export const fieldMissionHeaderSchema = z.object({
  id: uuidSchema,
  number: z.number().int().nullable(),
  status: fieldMissionStatusSchema,
  legacyStatus: z.string(),
  organizationId: uuidSchema.nullable(),
  season: z.string().nullable(),
  routeId: uuidSchema,
  routeName: z.string().nullable(),
  routeColor: z.string().nullable(),
  routeVersion: z.number().int().nullable(),
  routeDescription: z.string().nullable(),
  operatorId: uuidSchema.nullable(),
  operatorName: z.string().nullable(),
  equipmentId: uuidSchema.nullable(),
  equipmentLabel: z.string().nullable(),
  startedAt: timestampSchema,
  endedAt: nullableTimestampSchema,
  pausedAt: nullableTimestampSchema,
  startEngineHours: z.number().nonnegative(),
  endEngineHours: z.number().nonnegative().nullable(),
  endSummary: fieldProgressSchema.extend({ unresolved: z.boolean().optional() }).nullable(),
  cancelledReason: z.string().nullable(),
  progress: fieldProgressSchema,
  pausedSeconds: z.number().nonnegative(),
  /** Profil de détection figé au démarrage; absent (serveur antérieur) ou null : détection OFF. */
  detectionProfile: missionDetectionProfileSnapshotSchema.nullable().optional(),
  /** Opération de déneigement (ADR-019); absente (serveur antérieur) ou null : mission sans opération. */
  operationId: uuidSchema.nullable().optional(),
  operationNumber: z.string().nullable().optional(),
  missionKind: z.enum(['NORMAL', 'REINFORCEMENT']).optional(),
  cancelKind: z.enum(['ACCIDENTAL_START', 'ADMIN']).nullable().optional(),
  /** Parcours validé figé au démarrage et extrémités des liaisons; absent (serveur antérieur) : aucun parcours. */
  routeGuide: missionRouteGuideSchema.nullable().optional(),
})
export type FieldMissionHeader = z.infer<typeof fieldMissionHeaderSchema>

/** Instantané figé au démarrage : l'adresse ne change pas si la fiche du client change ensuite. */
export const fieldItemSnapshotSchema = z.object({
  addressLine1: z.string().nullable(),
  addressLine2: z.string().nullable(),
  city: z.string().nullable(),
  postalCode: z.string().nullable(),
  formattedAddress: z.string().nullable(),
  clientName: z.string().nullable(),
  contractNumber: z.string().nullable(),
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
  routeOrder: z.number().nullable(),
  /** Drapeau figé au démarrage; absent ou null : aucun drapeau, jamais dérivé d'une autre coordonnée. */
  flag: fieldItemFlagSchema.nullable().optional(),
  /** Rayon de capture figé à l'engagement de la résidence dans l'opération (tâches seulement). */
  captureRadiusM: z.number().positive().nullable().optional(),
})
export type FieldItemSnapshot = z.infer<typeof fieldItemSnapshotSchema>

export const fieldItemEventSchema = z.object({
  id: uuidSchema,
  kind: fieldItemEventKindSchema,
  fromStatus: fieldItemStatusSchema,
  toStatus: fieldItemStatusSchema,
  reasonCode: fieldProblemReasonSchema.nullable(),
  reasonText: z.string().nullable(),
  note: z.string().nullable(),
  occurredAt: timestampSchema,
  /** Absente (serveur antérieur) : maintien de 3 s (MANUAL). */
  source: completionSourceSchema.optional(),
  actorName: z.string().nullable(),
})
export type FieldItemEvent = z.infer<typeof fieldItemEventSchema>

export const fieldMissionItemSchema = z.object({
  id: uuidSchema,
  missionId: uuidSchema,
  order: z.number().int().positive(),
  status: fieldItemStatusSchema,
  itemVersion: z.number().int().positive(),
  serviceLocationId: uuidSchema,
  contractId: uuidSchema,
  snapshot: fieldItemSnapshotSchema,
  completedAt: nullableTimestampSchema,
  clearedAt: nullableTimestampSchema,
  clearedReason: z.string().nullable(),
  events: z.array(fieldItemEventSchema),
  // Renseignements terrain autorisés (tablette seulement). Jamais prix, paiement ni courriel.
  phone: z.string().nullable().optional(),
  locationInstructions: z.string().nullable().optional(),
  operatorMessage: z.string().nullable().optional(),
  specialInstructions: z.string().nullable().optional(),
  knownObstacles: z.string().nullable().optional(),
  /** Tâche d'opération (ADR-019). */
  operationTaskId: uuidSchema.nullable().optional(),
  /** Prise en charge par une autre mission : la résidence ne se termine plus ici. */
  transfer: z
    .object({ at: timestampSchema, toMissionId: uuidSchema, operatorName: z.string().nullable() })
    .nullable()
    .optional(),
})
export type FieldMissionItem = z.infer<typeof fieldMissionItemSchema>

export const operatorMissionHomeSchema = z.object({
  operator: z.object({ employeeId: uuidSchema, name: z.string().nullable() }),
  route: z
    .object({
      id: uuidSchema,
      name: z.string(),
      color: z.string().nullable(),
      season: z.string().nullable(),
      available: z.boolean(),
      eligibleCount: z.number().int().nonnegative(),
    })
    .nullable(),
  equipment: z
    .object({
      id: uuidSchema,
      number: z.string().nullable(),
      name: z.string(),
      available: z.boolean(),
      lastReading: hourReadingSchema.nullable(),
    })
    .nullable(),
  openMission: fieldMissionHeaderSchema.nullable(),
  blockers: z.array(fieldStartBlockerSchema),
  /** Opération ouverte de la saison, affichée avant le départ (ancienne : signalée, jamais doublée). */
  operation: z
    .object({
      id: uuidSchema,
      number: z.string(),
      status: z.enum(['OPEN', 'CLOSED', 'CANCELLED']),
      openedAt: timestampSchema,
      ageHours: z.number().nonnegative(),
      stale: z.boolean(),
    })
    .nullable()
    .optional(),
})
export type OperatorMissionHome = z.infer<typeof operatorMissionHomeSchema>

export const operatorMissionViewSchema = z.object({
  mission: fieldMissionHeaderSchema,
  items: z.array(fieldMissionItemSchema),
})
export type OperatorMissionView = z.infer<typeof operatorMissionViewSchema>

const replayedSchema = z.object({ replayed: z.boolean() })

export const startMissionResultSchema = replayedSchema.extend({
  missionId: uuidSchema,
  number: z.number().int().nullable(),
  status: fieldMissionStatusSchema,
  items: z.number().int().positive(),
  operationId: uuidSchema.optional(),
  operationNumber: z.string().optional(),
  /** 0.6.0 : passage engagé (décidé par le serveur) et route choisie. */
  passNumber: z.number().int().positive().optional(),
  routeId: uuidSchema.optional(),
})
export type StartMissionResult = z.infer<typeof startMissionResultSchema>

export const itemCommandResultSchema = replayedSchema.extend({
  itemId: uuidSchema,
  status: fieldItemStatusSchema,
  alreadyCompleted: z.boolean().optional(),
  itemVersion: z.number().int().positive(),
  progress: fieldProgressSchema,
  /** Résidence transférée entre-temps : le compte rendu est conservé comme conflit, rien n'est appliqué. */
  conflict: z
    .object({
      kind: z.enum(['COMPLETE_AFTER_TRANSFER', 'PROBLEM_AFTER_TRANSFER']),
      ownerName: z.string().nullable(),
      ownerMissionId: uuidSchema.nullable(),
    })
    .optional(),
})
export type ItemCommandResult = z.infer<typeof itemCommandResultSchema>

export const pauseResumeResultSchema = replayedSchema.extend({
  missionId: uuidSchema,
  status: fieldMissionStatusSchema,
  pausedAt: nullableTimestampSchema.optional(),
})
export type PauseResumeResult = z.infer<typeof pauseResumeResultSchema>

export const missionHeaderResultSchema = replayedSchema.extend({
  mission: fieldMissionHeaderSchema,
  /** Fin de mission : état de l'opération recalculé (ADR-019). */
  operationState: z.string().nullable().optional(),
  /** Annulation : tâches retirées, à réattribuer, opération annulée. */
  withdrawnTasks: z.number().int().nonnegative().optional(),
  tasksToReassign: z.number().int().nonnegative().optional(),
  operationCancelled: z.boolean().optional(),
})
export type MissionHeaderResult = z.infer<typeof missionHeaderResultSchema>

export const positionRejectionReasonSchema = z.enum([
  'INVALID',
  'FUTURE',
  'BEFORE_START',
  'AFTER_END',
  'DURING_PAUSE',
  'ID_CONFLICT',
])

export const positionsAckSchema = z.object({
  acknowledged: z.array(uuidSchema),
  rejected: z.array(z.object({ id: uuidSchema, reason: positionRejectionReasonSchema })),
  receivedAt: timestampSchema,
})
export type PositionsAck = z.infer<typeof positionsAckSchema>

export const gpsGapKindSchema = z.enum([
  'PERMISSION_DENIED',
  'POSITION_UNAVAILABLE',
  'APP_HIDDEN',
  'STALE_POSITION',
])
export type GpsGapKind = z.infer<typeof gpsGapKindSchema>

/** Relevé de position tel que la tablette le conserve et l'envoie. */
export const fieldPositionSchema = z.object({
  id: uuidSchema,
  capturedAt: timestampSchema,
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180),
  accuracy: z.number().nonnegative().nullable(),
  heading: z.number().min(0).lt(360).nullable().optional(),
  speed: z.number().nonnegative().nullable().optional(),
})
export type FieldPosition = z.infer<typeof fieldPositionSchema>

export const missionTrackSchema = z.object({
  missionId: uuidSchema,
  points: z.array(
    z.object({
      id: uuidSchema,
      capturedAt: timestampSchema,
      receivedAt: timestampSchema,
      latitude: z.number(),
      longitude: z.number(),
      accuracy: z.number().nullable(),
    }),
  ),
  pauses: z.array(z.object({ startedAt: timestampSchema, endedAt: nullableTimestampSchema })),
  gaps: z.array(
    z.object({ kind: gpsGapKindSchema, startedAt: timestampSchema, endedAt: nullableTimestampSchema }),
  ),
})
export type MissionTrack = z.infer<typeof missionTrackSchema>

// ------------------------------------------------------------------ Bureau

export const missionsDashboardSchema = z.object({
  inProgress: z.number().int().nonnegative(),
  paused: z.number().int().nonnegative(),
  completedToday: z.number().int().nonnegative(),
  completedTodayWithUnresolved: z.number().int().nonnegative(),
  unresolvedProblems: z.number().int().nonnegative(),
  today: z.string(),
})
export type MissionsDashboard = z.infer<typeof missionsDashboardSchema>

export const missionListRowSchema = z.object({
  id: uuidSchema,
  number: z.number().int().nullable(),
  status: fieldMissionStatusSchema,
  legacyStatus: z.string(),
  operatorName: z.string().nullable(),
  equipmentLabel: z.string().nullable(),
  routeName: z.string().nullable(),
  routeColor: z.string().nullable(),
  startedAt: timestampSchema,
  endedAt: nullableTimestampSchema,
  progress: fieldProgressSchema,
  lastPositionReceivedAt: nullableTimestampSchema,
})
export type MissionListRow = z.infer<typeof missionListRowSchema>

export const missionListResultSchema = z.object({
  rows: z.array(missionListRowSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
})
export type MissionListResult = z.infer<typeof missionListResultSchema>

export const missionListStatusFilterSchema = z.enum([
  'IN_PROGRESS',
  'PAUSED',
  'COMPLETED',
  'COMPLETED_UNRESOLVED',
  'CANCELLED',
])

export const missionDetailSchema = z.object({
  mission: fieldMissionHeaderSchema,
  items: z.array(fieldMissionItemSchema),
  pauses: z.array(z.object({ startedAt: timestampSchema, endedAt: nullableTimestampSchema })),
  readings: z.array(
    z.object({
      kind: z.enum(['START', 'END', 'CORRECTION', 'REPLACEMENT']),
      value: z.number().nonnegative(),
      recordedAt: timestampSchema,
      reason: z.string().nullable(),
    }),
  ),
  events: z.array(z.object({ type: z.string(), payload: z.unknown(), createdAt: timestampSchema })),
  gps: z.object({
    pointCount: z.number().int().nonnegative(),
    lastPosition: z
      .object({
        capturedAt: timestampSchema,
        receivedAt: timestampSchema,
        latitude: z.number(),
        longitude: z.number(),
        accuracy: z.number().nullable(),
      })
      .nullable(),
    lastReceivedAt: nullableTimestampSchema,
    gaps: z.array(
      z.object({
        kind: gpsGapKindSchema,
        startedAt: timestampSchema,
        endedAt: nullableTimestampSchema,
        receivedAt: timestampSchema,
      }),
    ),
  }),
})
export type MissionDetail = z.infer<typeof missionDetailSchema>

/** Données de la future Vue d'ensemble des opérations (aucun écran dans cette phase). */
export const missionsOverviewStateSchema = z.object({
  since: timestampSchema,
  missions: z.array(fieldMissionHeaderSchema),
  residences: z.array(
    z.object({
      itemId: uuidSchema,
      missionId: uuidSchema,
      missionStatus: fieldMissionStatusSchema,
      routeId: uuidSchema,
      routeColor: z.string().nullable(),
      serviceLocationId: uuidSchema,
      status: z.enum(['WAITING', 'PROBLEM']),
      snapshot: fieldItemSnapshotSchema,
      problemReason: fieldProblemReasonSchema.nullable(),
    }),
  ),
  equipmentPositions: z.array(
    z.object({
      equipmentId: uuidSchema.nullable(),
      equipmentLabel: z.string().nullable(),
      missionId: uuidSchema,
      capturedAt: timestampSchema,
      receivedAt: timestampSchema,
      latitude: z.number(),
      longitude: z.number(),
      accuracy: z.number().nullable(),
    }),
  ),
})
export type MissionsOverviewState = z.infer<typeof missionsOverviewStateSchema>

// ------------------------------------------------------------------ Compteur d'heures moteur

export type EngineHoursParseResult =
  | { ok: true; value: number; normalized: string }
  | { ok: false; reason: 'EMPTY' | 'NEGATIVE' | 'INVALID' | 'TOO_PRECISE' | 'TOO_LARGE' }

/**
 * Relevé cumulatif d'heures moteur saisi par l'opérateur : virgule ou point décimal, espaces de milliers tolérés,
 * au plus deux décimales. Jamais de valeur par défaut : une saisie vide est refusée.
 */
export function parseEngineHours(input: string): EngineHoursParseResult {
  const compact = input.replace(/[\s\u00a0\u202f]/g, '')
  if (compact === '') return { ok: false, reason: 'EMPTY' }
  if (compact.startsWith('-')) return { ok: false, reason: 'NEGATIVE' }
  const normalized = compact.replace(',', '.')
  if (!/^\d+(\.\d+)?$/.test(normalized)) return { ok: false, reason: 'INVALID' }
  const [whole = '', decimals = ''] = normalized.split('.')
  if (decimals.length > 2) return { ok: false, reason: 'TOO_PRECISE' }
  if (whole.replace(/^0+(?=\d)/, '').length > 8) return { ok: false, reason: 'TOO_LARGE' }
  const value = Number(normalized)
  return {
    ok: true,
    value,
    normalized: decimals ? `${String(Number(whole))}.${decimals}` : String(Number(whole)),
  }
}

// ------------------------------------------------------------------ Durées

export interface MissionDurations {
  /** Du départ à la fin (ou à `now` si la mission est ouverte). */
  elapsedSeconds: number
  pausedSeconds: number
  /** Écoulé moins pauses : temps de sortie active. */
  activeSeconds: number
  /** Relevé final moins relevé initial; nul tant que la sortie n'est pas terminée. Jamais déduit d'une durée. */
  engineHours: number | null
}

export function missionDurations(input: {
  startedAt: string
  endedAt: string | null
  pausedSeconds: number
  startEngineHours: number
  endEngineHours: number | null
  now?: Date
}): MissionDurations {
  const end = input.endedAt ? Date.parse(input.endedAt) : (input.now ?? new Date()).getTime()
  const elapsedSeconds = Math.max(0, Math.round((end - Date.parse(input.startedAt)) / 1000))
  const pausedSeconds = Math.min(elapsedSeconds, Math.max(0, Math.round(input.pausedSeconds)))
  const engineHours =
    input.endEngineHours === null
      ? null
      : Math.round((input.endEngineHours - input.startEngineHours) * 100) / 100
  return { elapsedSeconds, pausedSeconds, activeSeconds: elapsedSeconds - pausedSeconds, engineHours }
}

// ------------------------------------------------------------------ Trace GPS

export interface TrackPoint {
  id: string
  capturedAt: string
  latitude: number
  longitude: number
  accuracy: number | null
}

export interface TrackInterval {
  startedAt: string
  endedAt: string | null
}

export interface SegmentTrackOptions {
  /** Deux relevés plus espacés ne sont pas reliés (relevé visé toutes les 30 s). */
  maxGapMs?: number
  /** Vitesse implicite au-delà de laquelle un point est jugé aberrant (≈ 160 km/h). */
  maxSpeedMps?: number
  /** Précision annoncée au-delà de laquelle un point n'est pas tracé. */
  maxAccuracyM?: number
}

export interface SegmentedTrack {
  /** Tronçons à tracer séparément, chacun en ordre chronologique. */
  segments: TrackPoint[][]
  /** Points écartés : précision insuffisante ou saut aberrant. */
  discarded: { inaccurate: number; outliers: number }
  /** Coupures introduites (interruption, pause, écart trop long, aberration). */
  breaks: number
}

const earthRadiusM = 6_371_000

export function trackDistanceMeters(a: TrackPoint, b: TrackPoint): number {
  const toRad = (degrees: number) => (degrees * Math.PI) / 180
  const dLat = toRad(b.latitude - a.latitude)
  const dLng = toRad(b.longitude - a.longitude)
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.latitude)) * Math.cos(toRad(b.latitude)) * Math.sin(dLng / 2) ** 2
  return 2 * earthRadiusM * Math.asin(Math.min(1, Math.sqrt(h)))
}

function inside(time: number, intervals: TrackInterval[]): boolean {
  return intervals.some((interval) => {
    const start = Date.parse(interval.startedAt)
    const end = interval.endedAt ? Date.parse(interval.endedAt) : Number.POSITIVE_INFINITY
    return time >= start && time <= end
  })
}

function crosses(from: number, to: number, intervals: TrackInterval[]): boolean {
  return intervals.some((interval) => {
    const start = Date.parse(interval.startedAt)
    const end = interval.endedAt ? Date.parse(interval.endedAt) : Number.POSITIVE_INFINITY
    return start < to && end > from
  })
}

/**
 * Trace enregistrée (pas un itinéraire calculé) : relevés dans l'ordre chronologique, coupés aux pauses, aux
 * interruptions GPS déclarées, aux écarts trop longs et aux sauts aberrants. Jamais de ligne artificielle entre
 * deux tronçons.
 */
export function segmentTrack(
  points: TrackPoint[],
  interruptions: TrackInterval[] = [],
  options: SegmentTrackOptions = {},
): SegmentedTrack {
  const maxGapMs = options.maxGapMs ?? 120_000
  const maxSpeedMps = options.maxSpeedMps ?? 45
  const maxAccuracyM = options.maxAccuracyM ?? 100
  const ordered = [...points].sort(
    (a, b) => Date.parse(a.capturedAt) - Date.parse(b.capturedAt) || a.id.localeCompare(b.id),
  )
  const segments: TrackPoint[][] = []
  const discarded = { inaccurate: 0, outliers: 0 }
  let breaks = 0
  let current: TrackPoint[] = []
  // Point en saut aberrant mis de côté : si le suivant le confirme, c'était un vrai déplacement (nouveau tronçon).
  let suspect: TrackPoint | null = null
  const jump = (from: TrackPoint, to: TrackPoint) => {
    const gap = Date.parse(to.capturedAt) - Date.parse(from.capturedAt)
    const distance = trackDistanceMeters(from, to)
    const speed = gap > 0 ? distance / (gap / 1000) : Number.POSITIVE_INFINITY
    return speed > maxSpeedMps && distance > 50
  }
  const startSegment = () => {
    if (current.length > 0) segments.push(current)
    current = []
    breaks += 1
  }
  for (const point of ordered) {
    const time = Date.parse(point.capturedAt)
    if (point.accuracy !== null && point.accuracy > maxAccuracyM) {
      discarded.inaccurate += 1
      continue
    }
    if (inside(time, interruptions)) {
      discarded.outliers += 1
      continue
    }
    const previous = current.at(-1)
    if (previous && jump(previous, point)) {
      if (
        suspect &&
        !jump(suspect, point) &&
        Date.parse(point.capturedAt) - Date.parse(suspect.capturedAt) <= maxGapMs
      ) {
        startSegment()
        current.push(suspect, point)
        suspect = null
        continue
      }
      if (suspect) discarded.outliers += 1
      suspect = point
      continue
    }
    if (suspect) {
      discarded.outliers += 1
      suspect = null
    }
    if (previous) {
      const previousTime = Date.parse(previous.capturedAt)
      if (time - previousTime > maxGapMs || crosses(previousTime, time, interruptions)) startSegment()
    }
    current.push(point)
  }
  if (suspect) discarded.outliers += 1
  if (current.length > 0) segments.push(current)
  return { segments, discarded, breaks }
}
